/**
 * File Name          : physens-device.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { Subject, interval, Subscription, Observable, max } from "rxjs";
import { CommunicationType, AppResponsePacketPromise, PacketResponsePromise } from "../communication-types";
import { BLE_CHARGE_ICON, bleChargeIconToString } from "../battery_state_char.model";
import { ACCESS_CONTROL_ROLE } from "../file_system/access-control-role.model";
import { Drive_Item, DRIVE_ITEM_TYPE } from "../file_system/drive-item.model";
import { AppCommandPacket } from "../packets/app-command-packet.model";
import { AppResponsePacket } from "../packets/app-response-packet.model";
import { AppStatusPacket } from "../packets/app-status-packet.model";
import { CommandPacket, PACKET_SUB_TYPE } from "../packets/command-packet.model";
import { GenericPacket } from "../packets/generic-packet.model";
import { OUTPUTSET_TYPE, OutputsetPacket } from "../packets/outputset-packet.model";
import { Packet, PACKET_TYPE } from "../packets/packet.model";
import { ResponsePacket, FILERECORDER_STATE, RESPONSE_RESULT } from "../packets/response-packet.model";
import { RESULT_CODE } from "../packets/response-result.model";
import { ParameterSet } from "../parameters/parameter-set.model";
import { Publisher } from "../publishers/publisher.model";
import { DeviceFileSubscription } from "../subscription/device-file-sub.model";
import { FILE_ACCESS } from "../file_system/file-access.model";
import { FILE_OPEN_MODE } from "../file_system/file-open-mode.model";
import { Datasnap_MK1 } from "../../app-impls/datasnap-mk1";

// Single master switch for all OTA diagnostic ("[FWUpdate][DIAG]") logging across the app
// (firmwareupdate.component, idl-physens.service, physens-ble-device). Flip to false to mute
// every DIAG line at once without touching the OTA behaviour those logs instrument.
export const OTA_DIAG = true;

// While updateInProgress is true, the keepalive tick sends a single GET_BATCHRGSTATE request
// (sendOtaBatteryPoll). It is both the keepalive for the ~30s idle-link-loss problem during
// OTA uploads and the charger check: a device must stop communicating as soon as it is on the
// charger, OTA included (bug 20264). It used to be toggleable (OTA_KEEPALIVE); it no longer is,
// because turning it off would also turn off charger detection.

export class PhysensDevice {
  mtuLength = 400;
  manufacturerData: {[key: string]: DataView} | undefined
  removed = false;
  deviceId: string
  deviceName?: string
  deviceState: Device_State = Device_State.DISCONNECTED
  hasConnected = false;
  connectionAttempts = 0;
  maxConnectionAttempts = 0;
  connectAttemptLimitSubject = new Subject<void>()
  deviceStateSubject = new Subject<Device_State>()
  colorStatusClass: string = ""
  // lastHeartbeatTime = 0
  responding = true
  respondingStateSubject = new Subject<boolean>()
  // heartbeatCheck: ReturnType<typeof setTimeout>|null = null
  keepAliveInterval = interval(1000)
  keepAliveSubscription?: Subscription
  updateTimeInterval = interval(250)
  updateTimeIntervalSubscription?: Subscription
  connectionStatsInterval = interval(1000)
  connectionStatsIntervalSubscription?: Subscription
  lastRxCount = 0
  lastTxCount = 0
  rx_s = 0
  tx_s = 0
  packet_number = 0
  connectionStats = new ConnectionStatistics()
  packet_subject = new Subject<Packet|ResponsePacket|AppResponsePacket>();
  publishers: Publisher[] = []
  parameterSets: ParameterSet[] = []
  // rxPackets: Array<Packet|ResponsePacket> = []
  // txPackets: Array<Packet|ResponsePacket> = []
  packetHistory: [CommunicationType, Packet|Uint8Array][] = []
  tx_bytes = 0;
  device_info: {
    deviceSN: string,
    projectID: number,
    deviceType: number,
    roleID: number,
    appSpecInfo: number,
    fwReleaseType: string,
    fwMajorVersion: number,
    fwMinorVersion: number,
    fwBugVersion: number,
    hwProductID: number,
    hwSubcomponentID: number,
    hwMajorVersion: number,
    hwMinorVersion: number,
    dfuType: number,
    dfuMajorVersion: number,
    dfuMinorVersion: number,
    dfuBugVersion: number,
    bootType: number,
    bootMajorVersion: number,
    bootMinorVersion: number,
    bootBugVersion: number,
    compileTime: string,
  } | null = null;
  battery_state: BLE_CHARGE_ICON = BLE_CHARGE_ICON.NA;
  battery_state_string = bleChargeIconToString(this.battery_state);
  battery_state_subject = new Subject<BLE_CHARGE_ICON>();
  radio_state: number = 0;
  deviceTime: Date = new Date()
  deviceTimeError: number = 99999999;

  fileRecorderState: FILERECORDER_STATE | null = null;
  fileRecorderStateSubject = new Subject<FILERECORDER_STATE|null>();
  fileRecorderFileSize: number|null = null;
  fileRecorderWriteSpeed = 0;
  pollFileRecorderState = false;
  private fileRecorderStatsMissCount = 0;
  private fileRecorderPollActive = true;
  // Set while readFileWithProgress() is running so the recorder-stats poll doesn't compete
  // with the file-download traffic on the same BLE link (both were seen overlapping and
  // occasionally starving a GET_FILE_DATA response past its 30s timeout).
  private fileDownloadInProgress = false;
  lastFileRecorderStatsTime: number | null = null;

  logTxBytes = false;
  txBytesLog: Uint8Array = new Uint8Array(0);

  // Set while a firmware update is in progress so the periodic keepalive/battery/file-recorder
  // polling backs off instead of competing with the update's own BLE traffic (worst right around
  // the device reboot triggered by cmdReset during finalization).
  updateInProgress = false;
  // Narrower than updateInProgress: true only for the actual reset->disconnect->reconnect window
  // owned by firmwareupdate.component's rebootAndVerify() (set/cleared there via
  // DatasnapDevice.rebootInProgress), not the whole OTA flow including upload. A disconnect that
  // happens mid-upload (before any reset is sent) should still be governed by the normal
  // reconnect-retry safety nets instead of being exempted for the entire update's duration.
  expectingReboot = false;
  // Narrower still: true only for the read-only post-reconnect verify window (after the device
  // has already reconnected post-reboot, before SUCCESS/failure is decided) — set/cleared by
  // firmwareupdate.component's rebootAfterConfirm()/tapToFinishReconnect() around their
  // verifyActiveVersionReadOnly() calls (via DatasnapDevice.otaVerifying). expectingReboot is
  // already false by this point (the reconnect it was guarding has happened), so without this a
  // disconnect during verify would be misread as the mid-upload link-loss case even though it's
  // not "uploading" — exists purely so PhysensBleDevice.connect()'s OTA-stuck branch can exclude
  // this window too, never altering the existing verify/reboot logic itself.
  otaVerifying = false;

  constructor(deviceId: string, deviceName?: string, flags: {pollFileRecorderState?: boolean} = {}) {
    this.deviceId = deviceId
    this.deviceName = deviceName
    this.pollFileRecorderState = flags.pollFileRecorderState ?? false;
    setInterval(() => {
      this.checkPacketBuffer();
      this.clearPacketBuffer();
    }, 2);
  }

  remove() {
    this.removed = true
    this.keepAliveSubscription?.unsubscribe()
    this.updateTimeIntervalSubscription?.unsubscribe()
    this.connectionStatsIntervalSubscription?.unsubscribe()
  }

  resetConnectionAttempts() {
    this.connectionAttempts = 0;
  }

  connect(maxAttempts?: number): boolean {
    if(maxAttempts) {
      // A manual reconnect/reselect click always gets a fresh budget - without this,
      // automatic background retries firing between clicks (each disconnect increments
      // connectionAttempts regardless of who triggered the connect) silently eat into
      // whatever budget an earlier click armed, so a second click can find the limit
      // already exhausted and fail instantly.
      this.maxConnectionAttempts = maxAttempts;
      this.connectionAttempts = 0;
    }
    if(this.removed) return false;
    if(this.maxConnectionAttempts > 0 && this.connectionAttempts >= this.maxConnectionAttempts) {
      this.connectAttemptLimitSubject.next();
      // Notify (CONNECTION_TIMEOUT status) but don't brick reconnection forever: clearing
      // the limit here lets the automatic reconnect loop fall back to unlimited retries,
      // same as it always has for platforms that never call connect() with a maxAttempts
      // budget in the first place (e.g. Windows, which recovers on its own within a few
      // seconds of a stuck 96% OTA reboot). Without this, a manual "Reconnect" button
      // (Chrome's OTA fallback) permanently locks out BOTH manual and automatic connect()
      // calls the moment the shared counter crosses its budget, producing a perpetual
      // "Connecting -> instantly back to Reconnect" loop with no way to recover.
      this.maxConnectionAttempts = 0;
      return false;
    }
    return true;
  }

  updateDeviceState(state: Device_State) {
    this.deviceState = state

    // console.log(state)
    console.log("Device " + this.deviceName + " state: " + this.deviceState)

    switch(this.deviceState) {
      case Device_State.READY_FOR_PACKETS:
        this.connectionAttempts = 0
        this.maxConnectionAttempts = 0
        break;
      case Device_State.DISCONNECTED:
        this.connectionAttempts += 1
        break;
    }

    this.updateDeviceStateColor()
    this.deviceStateSubject.next(this.deviceState);
  }

  updateDeviceStateColor() {
    var colorStatusClass = ""
    switch(this.deviceState) {
      case Device_State.READY_FOR_PACKETS:
        colorStatusClass = (this.responding) ? "status-green" : "status-yellow"
        break;
      case Device_State.DISCONNECTED:
        colorStatusClass = "status-red"
        break;
      default:
        colorStatusClass = "status-yellow"
        break;

    }

    if(this.colorStatusClass != colorStatusClass) {
      this.colorStatusClass = colorStatusClass
    }
  }

  

  getMetaDataJson(): string {
    let metadata = {
      serialNumber: this.device_info?.deviceSN,
      projectID: this.device_info?.projectID,
      devType: this.device_info?.deviceType,
      roleID: this.device_info?.roleID,
      appSpec: this.device_info?.appSpecInfo,
      fwVersion: [this.device_info?.fwMajorVersion, this.device_info?.fwMinorVersion, this.device_info?.fwBugVersion].join('.'),
      hwVersion: [this.device_info?.hwMajorVersion, this.device_info?.hwMinorVersion].join('.'),
      deviceName: this.deviceName,
      publishers: []
    }

    for(let pub of this.publishers) {
      let pubJson = pub.asObject()
      for(let output of pub.outputs) {
        pubJson.outputs.push(output.asObject())
      }
      
    }

    return JSON.stringify(metadata)
  }

  getFwVersionString(): string {
    if(this.device_info) {
      return [this.device_info.fwMajorVersion, this.device_info.fwMinorVersion, this.device_info.fwBugVersion].join('.')
    }
    return "Unknown"
  }

  getAppCommandPacket(): AppCommandPacket {
    let pkt = new AppCommandPacket()
    pkt.mPacketNumber = this.packet_number++


    return pkt
  }

  sendAppCommandPacket(pkt: AppCommandPacket): AppResponsePacketPromise {

    return this.sendPacket(pkt) as AppResponsePacketPromise;
  }

  private enableFileRecorderHandles?: [(value: PromiseLike<null> | null)=>void,(value: PromiseLike<null> | null)=>void];

  // Shared rejection reason for cancelPendingHousekeeping() — lets the generic
  // failure logging below (and any other external .catch on these packets)
  // recognize an OTA-start cancellation and stay quiet, instead of logging a
  // spurious failure for a packet that was never actually sent.
  private static readonly OTA_CANCEL_REASON = 'OTA update starting';

  private sendPacketWithoutAwaiting(pkt: Packet|CommandPacket, context: string, responseTimeout=30000) {
    void this.sendPacket(pkt, responseTimeout).catch((error) => {
      if (error?.message === PhysensDevice.OTA_CANCEL_REASON) return;
      console.warn(`${context} failed:`, error);
    });
  }

  enableFileRecorder(recordName: string, enable: boolean): Promise<null> {
    let recordPromise = new Promise<null>((resolve, reject)=> {
      let pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.ENABLE_FILERECORDER
      
      pkt.portId = PORT_UUID.FILE0
      pkt.enableRecorder = enable
      pkt.recordName = recordName
  
      pkt.calcBytes()

      console.log(`TEST: Enable File Recorder: ${enable} ${recordName} ${Array.from(pkt.mBytes!).map(b => b.toString(16).padStart(2, '0')).join(' ')}`)

      this.enableFileRecorderHandles = [resolve, reject]

      void this.sendPacket(pkt).catch((error) => {
        if (this.enableFileRecorderHandles) {
          const [, rejectHandle] = this.enableFileRecorderHandles;
          this.enableFileRecorderHandles = undefined;
          rejectHandle(error);
        }
      })
    })

    return recordPromise

  }
  enableFileRecorderV2(recordName: string, enable: boolean, timeout_ms: number, timeout_filesize: number): PacketResponsePromise {
    let pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.ENABLE_FILERECORDER_V2;

    pkt.portId = PORT_UUID.FILE0;
    pkt.enableRecorder = enable;
    pkt.recordName = recordName;
    pkt.timeoutMs = timeout_ms;
    pkt.timeoutFilesize = timeout_filesize;
    pkt.timeoutAction = 0;
    pkt.calcBytes();

    console.log(`TEST: Enable File Recorder V2: ${enable} ${recordName} ${timeout_ms}ms ${timeout_filesize}bytes `)
    return this.sendPacket(pkt, 5000);
  }

  getFileRecorderStatus(port: PORT_UUID): PacketResponsePromise {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.GET_FILERECORDER_STATS;
    
    pkt.portId = port;
    pkt.calcBytes();

    const pktPromise =  this.sendPacket(pkt);

    void pktPromise.then(([resp_pkt, sent_pkt, timing])=> {
      this.fileRecorderState = resp_pkt.fileRecorderState
      if(this.fileRecorderFileSize != null) {
        this.fileRecorderWriteSpeed = (resp_pkt.fileSize??0) - this.fileRecorderFileSize
      }
      this.fileRecorderFileSize = resp_pkt.fileSize
      
      this.fileRecorderStateSubject.next(this.fileRecorderState);
    }, () => undefined);

    return pktPromise
  }

  sendTagData(markerTimeUs: number|null, tag: string) {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.TAG_DATA

    pkt.portId = PORT_UUID.ALL
    pkt.markerTimeUs = markerTimeUs ?? 0xFFFFFFFF
    pkt.tagName = tag

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Tag data send")
  }

  getPublishers() {
    this.publishers = []
    console.log("getPublishers")
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.GET_PUBLISHERS

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Get publishers")
  }

  getPublisherNames() {
    console.log("getPublisherNames")
    let pktsToSend = []

    for(let publisher of this.publishers) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_PUBLISHER_NAME
      pkt.publisherId = publisher.pubId

      pkt.calcBytes()

      pktsToSend.push(pkt)
    }

    this.sendPackets(pktsToSend)
  }

  getOutputs() {
    console.log("getOutputs")
    let pktsToSend = []
    for(let publisher of this.publishers) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_OUTPUTS
      pkt.publisherId = publisher.pubId

      pkt.calcBytes()

      pktsToSend.push(pkt)
    }

    this.sendPackets(pktsToSend)
  }

  getOutputNames(pubId: number) {
    console.log("getOutputNames")
    let pktsToSend = []
    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == pubId})
    if(pub != null) {
      for(let output of pub!.outputs) {
        var pkt = new CommandPacket()
        pkt.mPacketNumber = this.packet_number++
        pkt.pktSubType = PACKET_SUB_TYPE.GET_OUTPUT_NAME
        pkt.publisherId = pub.pubId
        pkt.outputIdx = output.index

        pkt.calcBytes()

        pktsToSend.push(pkt)
      }

      this.sendPackets(pktsToSend)
    }
  }

  getOutputUnits(pubId: number) {
    console.log("getOutputUnits")
    let pktsToSend = []
    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == pubId})
    if(pub != null) {
      for(let output of pub!.outputs) {
        var pkt = new CommandPacket()
        pkt.mPacketNumber = this.packet_number++
        pkt.pktSubType = PACKET_SUB_TYPE.GET_OUTPUT_UNITS
        pkt.publisherId = pub.pubId
        pkt.outputIdx = output.index

        pkt.calcBytes()

        pktsToSend.push(pkt)
      }

      this.sendPackets(pktsToSend)
    }
  }

  setAccessControlRole(role: ACCESS_CONTROL_ROLE, password?: string) {
    console.log("setAccessControlRole()")
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.ACTIVATE_ROLE

    pkt.accessControlRole = role
    pkt.password = (password) ? password : ""

    pkt.calcBytes()

    return this.sendPacket(pkt)
  }

  getParameterSetList() {
    console.log("getParameterSetList()")

    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_SETS

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Get parameter set list")
  }

  getParametersForSets() {
    let pktsToSend = []
    for(let paramSet of this.parameterSets) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETERS
      pkt.paramSetUUID = ((paramSet.setTypeId)<<4)|paramSet.instances[0]

      pkt.calcBytes()
      pktsToSend.push(pkt)

    }

    this.sendPackets(pktsToSend)
  }
  getInfoForParameterSets() {
    for(let paramSet of this.parameterSets) {
      console.log("getInfoForParameterSet " + paramSet.setTypeId.toString(16))
      this.getParameterSetInfo(paramSet).then(([resp_pkt, sent_pkt, timing])=> {
        console.log("got info for parameter set " + paramSet.setTypeId.toString(16))
        if(resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PARAMETER_SET_INFO) { 
            paramSet.name = resp_pkt.paramSetName!;
            paramSet.accessZone = resp_pkt.accessZone!;
        }
      }).catch((error)=>{
        console.error("Error getting parameter set info for " + paramSet.setTypeId.toString(16) + ": " + error)
        }
      );
    }
  }

  getParameterSetInfo(paramSet: ParameterSet) {
    var pkt = new CommandPacket(); 
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_SET_INFO;
    pkt.paramSetUUID = ((paramSet.setTypeId)<<4)|paramSet.instances[0];
    pkt.calcBytes();

    return this.sendPacket(pkt, 2000);

  }

  getParameterInfoForSet(paramSet: ParameterSet) {
    let pktsToSend = []
    for(let param of paramSet.parameters) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_INFO
      pkt.paramUUID = ((paramSet.setTypeId)<<20)|((paramSet.instances[0]&&0x0F)<<16)|param.UUID
      console.log("getting info for: " + pkt.paramUUID.toString(16))

      pkt.calcBytes()

      pktsToSend.push(pkt)

    }
    this.sendPackets(pktsToSend)
  }

  // getParametersList() {
  //   console.log("getParametersList")

  //   var pkt = new CommandPacket()
  //   pkt.mPacketNumber = this.packet_number++
  //   pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_LIST

  //   pkt.calcBytes()

  //   this.sendPacket(pkt)
  // }

  // getParametersInfo() {
  //   let pktsToSend = []
  //   for(let param of this.parameters) {
  //     var pkt = new CommandPacket()
  //     pkt.mPacketNumber = this.packet_number++
  //     pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_INFO
  //     pkt.paramUUID = param.UUID

  //     pkt.calcBytes()

  //     pktsToSend.push(pkt)

  //   }
  //   this.sendPackets(pktsToSend)
  // }

  getParameterVals(paramUUIDs: number[]) {
    let pktsToSend = []
    for(let paramUUID of paramUUIDs) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_PARAMETER_VAL
      pkt.paramUUID = paramUUID

      pkt.calcBytes()

      pktsToSend.push(pkt)

    }
    this.sendPackets(pktsToSend)
  }

  setParameterVals(paramUpdates: {uuid: number, value: Uint8Array}[]){
    let pktsToSend = []
    for(let paramUpdate of paramUpdates) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.SET_PARAMETER_VAL
      pkt.paramUUID = paramUpdate.uuid
      pkt.paramVal = paramUpdate.value

      pkt.calcBytes()

      pktsToSend.push(pkt)

    }
    this.sendPackets(pktsToSend)
  }

  setParameterVal(paramUpdate: {uuid: number, value: Uint8Array}): PacketResponsePromise {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SET_PARAMETER_VAL
    pkt.paramUUID = paramUpdate.uuid
    pkt.paramVal = paramUpdate.value

    pkt.calcBytes()
    return this.sendPacket(pkt);
  }

  saveAllParametersFile(){
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SAVE_ALL_PARAMETERS
    pkt.calcBytes()

    return this.sendPacket(pkt, 5000)
  }

  saveAllParametersMfg(){
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SAVE_ALL_PARAMETERS_MFG;
    pkt.calcBytes()
    return this.sendPacket(pkt, 5000)
  }

  filesystemGetDriveList() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.GET_DRIVE_LIST;
    pkt.calcBytes();

    // 10s was too tight right after a reconnect (device can still be settling);
    // align with the default response timeout used elsewhere.
    return this.sendPacket(pkt, 30000);
  }

  readNextDriveItem(driveIdx: number): PacketResponsePromise {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DIR_READ_NEXT;

    pkt.driveIdx = driveIdx;
    pkt.calcBytes();
    return this.sendPacket(pkt, 10000);
  }

  openDirectory(driveIdx: number, dirPath: string): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.DIR_OPEN);
    pkt.driveIdx = driveIdx;
    pkt.dirPath = dirPath;

    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  reformatDrive(driveIdx: number): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.REFORMAT_DRIVE);
    pkt.driveIdx = driveIdx;

    pkt.calcBytes();
    // Erasing a full flash drive can take well over the default 30s response timeout.
    return this.sendPacket(pkt, 120000);
  }


  openFile(driveIdx: number, filePath: string, fileAccessType: FILE_ACCESS, fileAccessMode: FILE_OPEN_MODE): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.FILE_OPEN);
    pkt.driveIdx = driveIdx;
    pkt.filePath = filePath;
    pkt.fileAccessType = fileAccessType;
    pkt.fileAccessMode = fileAccessMode;

    pkt.calcBytes();
    return this.sendPacket(pkt);
  }
  closeFile(driveIdx: number, fileIdx: number): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.FILE_CLOSE);
    pkt.driveIdx = driveIdx;
    pkt.fileIdx = fileIdx;
    pkt.calcBytes();
    return this.sendPacket(pkt, 5000);
  }

  getFileHash(driveIdx: number, fileIdx: number) {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.GET_FILE_HASH);
    pkt.driveIdx = driveIdx;
    pkt.fileIdx = fileIdx;
    pkt.numBytesToHash = 0; //gets all
    pkt.maxRxSize = 0; //unlimited?
    pkt.calcBytes();

    return this.sendPacket(pkt, 30000);
  }
  getFileData(driveIdx: number, fileIdx: number, start_address: number, max_rx_size: number): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.GET_FILE_DATA);
    pkt.driveIdx = driveIdx;
    pkt.fileIdx = fileIdx;
    pkt.startAddress = start_address;
    pkt.maxRxSize = max_rx_size;

    pkt.calcBytes();
    return this.sendPacket(pkt, 1000);
  }

  writeFileData(driveIdx: number, fileIdx: number, start_address: number, data: Uint8Array): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.FILE_WRITE);
    pkt.driveIdx = driveIdx;
    pkt.fileIdx = fileIdx;
    pkt.controlFlags = 0;
    pkt.startAddress = start_address;
    pkt.chunkHash = 0
    pkt.fileData = data;

    pkt.calcBytes();
    return this.sendPacket(pkt, 5000);
  }

  deleteFile(driveIdx: number, filePath: string): PacketResponsePromise {
    var pkt = new CommandPacket(this.packet_number++, PACKET_SUB_TYPE.FILE_REMOVE);
    pkt.driveIdx = driveIdx
    pkt.filePath = filePath;
    pkt.calcBytes();

    return this.sendPacket(pkt, 10000);
  }

  async mapDirectory(driveIdx: number, dirPath: string): Promise<Drive_Item[]> {
    
    //open the directory
    try { 
      await this.openDirectory(driveIdx, dirPath)
    } catch (e: any) {
      console.error("Failed to open directory:", e);
      throw new Error(`Failed to open directory: ${dirPath}`);
    }

    //Map out all of the items in this directory
    let lastItemType;

    let dirItems: Drive_Item[] = []

    do {
      let retries = 3;
      while (retries > 0) {
        try {
          const [result, _, responseStats] = await this.readNextDriveItem(driveIdx);
          if(result.mPktSubType == PACKET_SUB_TYPE.BAD_PACKET ) {
            throw new Error("Failed to read the next drive item with code: " + result.result_code);

          }
          lastItemType = result.driveItem?.type;
          dirItems.push({
            ...result.driveItem!,
            path: dirPath + '/' + result.driveItem?.name
          });
          break; // Success, exit retry loop
        } catch (e: any) {
          retries--;
          console.warn(`Error reading drive item. Retries left: ${retries}`, e);
          if (retries === 0) {
            throw new Error(`Failed to read drive item after 3 retries.`);
          }
        }
      }
    } while (lastItemType !== DRIVE_ITEM_TYPE.END_OF_DIR);

    for (let i = 0; i < dirItems.length; i++) {
      const value = dirItems[i];
      if ((value.type === DRIVE_ITEM_TYPE.DIR_ITEM) && (value.name !== '.' && value.name !== '..')) {
        value.children = await this.mapDirectory(driveIdx, [dirPath, value.name].join('/'));
      }
    }

    return dirItems;
  }

  readFileWithProgress(
    driveIdx: number,
    fileItem: Drive_Item
  ): Observable<{ progress: number, averageResponseTime?: number, averageCommandTime?: number, data?: Uint8Array, hashMatch?: boolean }> {
    return new Observable(observer => {
      (async () => {
        this.fileDownloadInProgress = true;
        try {
          console.log("Reading file: ", fileItem.path, "with size: ", fileItem.size);
          const [fileResponse, _, fileResponseStats] = await this.openFile(driveIdx, fileItem.path!, FILE_ACCESS.READ_ONLY, FILE_OPEN_MODE.EXISTING);
          if (fileResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to open file with code: ${fileResponse.result_code}`);
          }
          const fileIdx = fileResponse.fileIdx!;
          const [fileHashResponse, _a, fileHashResponseStats] = await this.getFileHash(driveIdx, fileIdx);
          if (fileHashResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to get file hash with code: ${fileHashResponse.result_code}`);
          }

          

          const fileData: Uint8Array = new Uint8Array(fileItem.size);
          let readIdx = 0;

          let responseTimes = [];
          let commandTimes = [];

          let commandStartTime = null;
          let lastPercentage = 0;

          let errorCount = 0;
          let errorCode: RESULT_CODE| null = null;

          while (readIdx < fileItem.size) {
            if (errorCount >= 3) {
                observer.error(`Failed to read file data after multiple attempts. Last error code: ${errorCode}. Read index: ${readIdx}`);
                return;
            }
            try {
              if(commandStartTime !== null) {
                commandTimes.push(performance.now() - commandStartTime);
              }
              
              const [dataResponse, _, dataResponseStats] = await this.getFileData(driveIdx, fileIdx, readIdx, 2048);
              
              if (dataResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
                errorCode = dataResponse.result_code
                errorCount++;
              } else if (dataResponse.mPktSubType !== PACKET_SUB_TYPE.GET_FILE_DATA) {
                errorCode = RESULT_CODE.NA
                errorCount++;
                
              } else {
                errorCode = null
                errorCount = 0;

                commandStartTime = performance.now();
                responseTimes.push(dataResponseStats.elapsedTime);

                const data = dataResponse.fileData!;
                const startAddress = dataResponse.startAddress!;
                const data_size = dataResponse.data_size!;

                // if(data_size < 2048) {
                //   console.warn(`Received data chunk smaller than requested: expected up to 2048, received ${data_size}, start address: ${startAddress}, read index: ${readIdx}`);
                // }
                if (data.length != data_size) {
                  console.warn(`Data size mismatch: expected ${data_size}, received ${data.length}, start address: ${startAddress}, read index: ${readIdx}`);
                  throw new Error(`Data size mismatch: expected ${data_size}, received ${data.length}`);
                }
                if (startAddress !== readIdx) {
                  console.warn(`Start address mismatch: expected ${readIdx}, received ${startAddress}, expected data size: ${data_size}, read index: ${readIdx}`);
                  throw new Error(`Start address mismatch: expected ${readIdx}, received ${startAddress}`);
                }

                fileData.set(data, readIdx);
                readIdx += data_size;

                const percent = Math.min(100, Math.floor((readIdx / fileItem.size) * 100));
                if(readIdx / fileItem.size > 1) {
                  console.warn("got too many bytes")
                }
                if( percent !== lastPercentage) {
                  observer.next({ progress: percent });
                  lastPercentage = percent;
                }
              }

            } catch (e: any) {
              console.error("Error reading file data:", e);
              if(this.deviceState !== Device_State.READY_FOR_PACKETS) {
                console.warn(`[BLE][${this.deviceName ?? this.deviceId}] lost connection mid-download; waiting for reconnect instead of spending a retry`);
                const reconnected = await this.waitForReconnect();
                if(!reconnected) {
                  observer.error(`Failed to read file data: connection was not restored. Read index: ${readIdx}`);
                  return;
                }
                console.log(`[BLE][${this.deviceName ?? this.deviceId}] reconnected mid-download, resuming read at index ${readIdx}`);
                continue;
              }
              errorCode = RESULT_CODE.NA
              errorCount++;
            }
            
          }

          const [fileCloseResponse, _b, fileCloseResponseStats] = await this.closeFile(driveIdx, fileResponse.fileIdx!);
          if (fileCloseResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to close file with code: ${fileCloseResponse.result_code}`);
          }

          const avgResponseTime = responseTimes.length > 0 ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length : undefined;
          const avgCommandTime = commandTimes.length > 0 ? commandTimes.reduce((a, b) => a + b, 0) / commandTimes.length : undefined;

          //calculate hash
          let remainder = 0x00;

          const fileDataArray = new Uint8Array(fileData);
          for (const byte of fileDataArray) {
              let datum = (byte & 0xff) ^ ((remainder & 0xffff) >> 8);
              remainder = ((Packet.crcTable[(datum + 256) % 256] || 0) ^ ((remainder << 8) & 0xffff)) & 0xffff;
          }

          const hashDigest = (remainder ^ 0x0000) & 0xffff;
          const expectedHashMasked = (fileHashResponse.hash_details?.digest||0) & 0xffff;
          if (expectedHashMasked !== hashDigest ) {
            console.warn(`File hash mismatch: expected ${expectedHashMasked}, calculated ${hashDigest}`);
          } else {
            console.log("File hash matches: ", expectedHashMasked);
          }

          observer.next({ progress: 100, averageResponseTime: avgResponseTime, averageCommandTime: avgCommandTime, data: fileData, hashMatch: expectedHashMasked === hashDigest });

          

          observer.complete();

        } catch (e: any) {
          console.error("Error reading file:", e);
          observer.error(e);
        } finally {
          this.fileDownloadInProgress = false;
        }
      })();
    });
  }

  writeFileWithProgress(driveIdx: number, path: string, fileData: ArrayBuffer): Observable<{ progress: number }> {
    return new Observable(observer => {
      (async () => {
        let fileIdx = null;

        try {
          this.logTxBytes = true;
          //open file
          const [fileResponse, _, fileResponseStats] = await this.openFile(driveIdx, path, FILE_ACCESS.READ_WRITE, FILE_OPEN_MODE.ALWAYS_OVERWRITE);
          if (fileResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to open file with code: ${fileResponse.result_code}`);
          }

          let writeIdx = 0;

          let lastPercentage = 0;
          let errorCount = 0;
          let errorCode: RESULT_CODE| null = null;
          fileIdx = fileResponse.fileIdx!;

          while (writeIdx < fileData.byteLength) {

            const [writeResponse, _, writeResponseStats] = await this.writeFileData(
              driveIdx,
              fileResponse.fileIdx!,
              writeIdx,
              new Uint8Array(fileData, writeIdx, Math.min(480, fileData.byteLength - writeIdx))
            );
            let nextWriteIdx = writeIdx + Math.min(480, fileData.byteLength - writeIdx);

            if (writeResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
              errorCode = writeResponse.result_code
              errorCount++;
            } else if (writeResponse.mPktSubType !== PACKET_SUB_TYPE.FILE_WRITE) {
              errorCode = RESULT_CODE.NA
              errorCount++;

            } else {
              errorCode = null;
              errorCount = 0;

              writeIdx = writeResponse.file_position!;
              if(writeIdx !=  nextWriteIdx) {
                console.warn(`Write index mismatch: expected ${nextWriteIdx}, got ${writeIdx}`);
              }
              const percent = Math.min(100, Math.floor((writeIdx / fileData.byteLength) * 100));
              if(writeIdx / fileData.byteLength > 1) {
                console.warn("got too many bites")
              }
              if( percent !== lastPercentage) {
                observer.next({ progress: percent });
                lastPercentage = percent;
              }
            }

            if (errorCount >= 3) {
                observer.error(`Failed to write file data after multiple attempts. Last error code: ${errorCode}`);
                return;
            }

          }

          
          //Check that upload hash matches
          const [fileHashResponse, _a, fileHashResponseStats] = await this.getFileHash(driveIdx, fileResponse.fileIdx!);
          if (fileHashResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to get file hash with code: ${fileHashResponse.result_code}`);
          }

          
          const [fileCloseResponse, _b, fileCloseResponseStats] = await this.closeFile(driveIdx, fileResponse.fileIdx!);
          if (fileCloseResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
            throw new Error(`Failed to close file with code: ${fileCloseResponse.result_code}`);
          }
          fileIdx = null;

          //calculate hash
          let remainder = 0x00;

          for (const byte of new Uint8Array(fileData)) {
              let datum = (byte & 0xff) ^ ((remainder & 0xffff) >> 8);
              remainder = ((Packet.crcTable[(datum + 256) % 256] || 0) ^ ((remainder << 8) & 0xffff)) & 0xffff;
          }
          const hashDigest = (remainder ^ 0x0000) & 0xffff;
          const expectedHashMasked = (fileHashResponse.hash_details?.digest||0) & 0xffff;

          if (expectedHashMasked !== hashDigest) {
            console.warn(`File hash mismatch: expected ${expectedHashMasked}, calculated ${hashDigest}`);
            observer.error(`File hash mismatch after write: expected ${expectedHashMasked}, calculated ${hashDigest}`);
          } else {
            console.log("File hash matches");
          }

          observer.next({ progress: 100 });
          observer.complete();
        } catch (e: any) {
          console.error("Error writing file:", e);
          observer.error(e);
        } finally {
          this.logTxBytes = false;
          //download log
          // const blob = new Blob([this.txBytesLog as any], { type: 'application/octet-stream' });
          // const url = window.URL.createObjectURL(blob);
          // const a = document.createElement('a');
          // a.href = url;
          // a.download = "tx_bytes_log.bin";
          // document.body.appendChild(a);
          // a.click();
          // document.body.removeChild(a);
          // this.txBytesLog = new Uint8Array(0);
        }
        if(fileIdx !== null) {
          try {
            await this.closeFile(driveIdx, fileIdx);
          } catch (e: any) {
            console.error("Error closing file after write error:", e);
          }
        }
      })();
    });
  }

  getDevInfo() {
    var pkt = new CommandPacket()
        pkt.mPacketNumber = this.packet_number++
        pkt.pktSubType = PACKET_SUB_TYPE.GET_DEVINFO

        pkt.calcBytes()

        return this.sendPacket(pkt)
  }

  getTime(): PacketResponsePromise {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.GET_TIME

    pkt.calcBytes()

    return this.sendPacket(pkt, 3000)
  }

  getDate() {
      // Guards against the deferred follow-up timer in setDate() below firing in the
      // same tick updateInProgress flips true, before cancelPendingHousekeeping() has
      // a chance to clearTimeout() it — belt-and-suspenders alongside that clear.
      if(this.updateInProgress) return;

      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.GET_DATE

      pkt.calcBytes()

      this.sendPacketWithoutAwaiting(pkt, "Get date")
  }

  // Tracks the setDate() follow-up timer below so cancelPendingHousekeeping() can cancel
  // it outright on OTA start — unlike an already-queued packet, a not-yet-fired timeout
  // isn't touched by clearPendingWrites() and would otherwise create a brand new GET_DATE
  // packet well after the cancel ran, which then dies with "Device disconnected" when the
  // reset tears the link down a moment later.
  private pendingDateCheckTimer: ReturnType<typeof setTimeout> | null = null;

  setDate(year: number, month: number, day:number, weekday: number) {
    if(this.updateInProgress) return;

    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SET_DATE

    pkt.year = year
    pkt.month = month
    pkt.day = day
    pkt.weekday = weekday

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Set date")

    if(this.pendingDateCheckTimer !== null) clearTimeout(this.pendingDateCheckTimer);
    this.pendingDateCheckTimer = setTimeout(()=>{
      this.pendingDateCheckTimer = null;
      this.getDate();
    }, 2000)
  }

  setTime(hour:number, minute:number, seconds:number, milliseconds:number) {
    if(this.updateInProgress) return;

    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SET_TIME

    pkt.hour = hour
    pkt.minute = minute
    pkt.second = seconds
    pkt.fracSeconds = Math.floor(milliseconds*255/1000)

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Set time")
  }

  getBatChargeState() {
    // console.log("sending getBatChargeState")
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.GET_BATCHRGSTATE;
    pkt.calcBytes();
    const pktResponse = this.sendPacket(pkt);
    void pktResponse.then(([resp_pkt, sent_pkt, timing])=> {
      this.publishBatteryState(resp_pkt.batteryState!);
    }, () => undefined);

    return pktResponse;
  }

  powerOff() {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SET_POWER

    pkt.powerLevel = 0x04

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Power off")
  }

  reset() {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.SET_POWER

    pkt.powerLevel = 0x05
    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Reset device")
  }

  identifyDevice(led_pattern: {
        red: number;
        green: number;
        blue: number;
        durationUs: number;
        blinkRate: number;
    }) {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.IDENTIFY
    pkt.led_pattern = led_pattern
    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Identify device")
  }

  setLEDColor(led_color: {red:number;green:number;blue:number;}) {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.SET_COLOR;
    pkt.led_color = led_color;
    pkt.calcBytes();

    return this.sendPacket(pkt);
  }

  getSystemStatus() {
    if(this.updateInProgress) return Promise.reject(new Error(PhysensDevice.OTA_CANCEL_REASON));

    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.GET_SYSTEM_STATUS;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  unsubscribeAll(port: PORT_UUID) {
    var pkt = new CommandPacket()
    pkt.mPacketNumber = this.packet_number++
    pkt.pktSubType = PACKET_SUB_TYPE.PORT_UNSUBSCRIBE_ALL

    pkt.destinationPortID = port

    pkt.calcBytes()

    this.sendPacketWithoutAwaiting(pkt, "Unsubscribe all")
  }

  subscribePublishers(pubs: DeviceFileSubscription[], port: PORT_UUID, subType: OUTPUTSET_TYPE = OUTPUTSET_TYPE.MIXED) {
    var pkts = []
    
    for(let pub of pubs) {
      var pkt = new CommandPacket()
      pkt.mPacketNumber = this.packet_number++
      pkt.pktSubType = PACKET_SUB_TYPE.SUBSCRIBE_BY_ID

      pkt.publisherId = pub.pubId
      pkt.destinationPortID = port
      pkt.subscriberType = subType
      pkt.downSample = pub.ds

      pkt.calcBytes()

      console.log(`TEST: Subscribe Publishers: pubId:${pub.pubId} port:${port} ${Array.from(pkt.mBytes!).map(b => b.toString(16).padStart(2, '0')).join(' ')}`)


      pkts.push(pkt)
    }

    this.sendPackets(pkts)
  }

  DFUGetStatus() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_GET_STATUS;
    pkt.calcBytes();

    return this.sendPacket(pkt);
  }

  DFUConfirmImage() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_CONFIRM_IMAGE;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  DFUTestImage() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_TEST_IMAGE;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  DFUEraseImage() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_ERASE_IMAGE;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  DFULoadImage() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_LOAD_IMAGE;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  DFUUploadImage(writeIdx: number, dataChunk: Uint8Array) {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_UPLOAD_IMAGE;
    pkt.fileData = dataChunk;
    pkt.startAddress = writeIdx;
    pkt.calcBytes();
    return this.sendPacket(pkt, 5000);
  }

  UploadDFUImage(fileData: ArrayBuffer): Observable<{ progress: number }> {
    return new Observable(observer => {
      (async () => {

        try {

          // //open file
          // const [fileResponse, _, fileResponseStats] = await this.openFile(driveIdx, path, FILE_ACCESS.READ_WRITE, FILE_OPEN_MODE.ALWAYS_OVERWRITE);
          // if (fileResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
          //   throw new Error(`Failed to open file with code: ${fileResponse.result_code}`);
          // }

          let writeIdx = 0;

          let lastPercentage = 0;
          let errorCount = 0;
          let errorCode: RESULT_CODE| null = null;

          while (writeIdx < fileData.byteLength) {

            const [writeResponse, _, writeResponseStats] = await this.DFUUploadImage(
              writeIdx,
              new Uint8Array(fileData, writeIdx, Math.min(480, fileData.byteLength - writeIdx))
            );

            if (writeResponse.mPktSubType === PACKET_SUB_TYPE.BAD_PACKET) {
              errorCode = writeResponse.result_code
              errorCount++;
            } else if (writeResponse.mPktSubType !== PACKET_SUB_TYPE.DFU_UPLOAD_IMAGE) { //TODO
              errorCode = RESULT_CODE.NA
              errorCount++;
            } else {
              errorCode = null;
              errorCount = 0;

              writeIdx = writeResponse.file_position!;
              const percent = Math.min(100, Math.floor((writeIdx / fileData.byteLength) * 100));
              if(writeIdx / fileData.byteLength > 1) {
                console.warn("got too many bites")
              }
              if( percent !== lastPercentage) {
                observer.next({ progress: percent });
                lastPercentage = percent;
              }
            }

            if (errorCount >= 3) {
                observer.error(`Failed to write file data after multiple attempts. Last error code: ${errorCode}`);
                return;
            }

          }

          observer.next({ progress: 100 });
          observer.complete();
        } catch (e: any) {
          console.error("Error writing file:", e);
          observer.error(e);
        }
        
      })();
    });
  }

  DFUGetImageList() {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_GET_IMAGE_LIST;
    pkt.calcBytes();

    return this.sendPacket(pkt);
  }

  DFUGetImageInfo(imageIdx: number) {
    var pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.DFU_GET_IMAGE_INFO;
    pkt.imageIdx = imageIdx;
    pkt.calcBytes();
    return this.sendPacket(pkt);
  }

  sendGenericPacket(pktParams: GenericPacket) {
    var pkt = new Packet();
    pkt.mPacketNumber == this.packet_number++;
    pkt.mPacketType = pktParams.mPacketType;
    pkt.pktSubType = pktParams.mPacketSubType;
    pkt.mPayloadLength = pktParams.mPayloadLength;
    pkt.payloadBytes = pktParams.payloadBytes;

    let calcSuccess = pkt.calcBytes();

    if(calcSuccess) return this.sendPacket(pkt);
    return undefined;
  }



  startIntervalSubscriptions() {
    this.fileRecorderStatsMissCount = 0;
    this.fileRecorderPollActive = true;

    this.keepAliveSubscription = this.keepAliveInterval.subscribe(()=>{
        if(this.updateInProgress) {
          void this.sendOtaBatteryPoll();
          return;
        }

        this.getTime().then(()=>{
          // console.log("Received Get Time Response")
          //success
          if(!this.responding) {
            this.responding = true;
            this.respondingStateSubject.next(this.responding);
            this.updateDeviceStateColor();
          }
        }).catch(()=>{
          //failed
          if(this.responding) {
            this.responding = false;
            this.respondingStateSubject.next(this.responding);
            this.updateDeviceStateColor();
          }
        });
        void this.getBatChargeState().catch(() => undefined);
        if(this.pollFileRecorderState && this.fileRecorderPollActive && !this.fileDownloadInProgress) {
          void this.getFileRecorderStatus(PORT_UUID.FILE0).then(() => {
            this.fileRecorderStatsMissCount = 0;
            this.lastFileRecorderStatsTime = Date.now();
          }).catch(() => {
            this.fileRecorderStatsMissCount++;
            if(this.fileRecorderStatsMissCount >= 3) {
              this.fileRecorderPollActive = false;
              console.debug(`[BLE][${this.deviceName ?? this.deviceId}] stall detected: no GET_FILERECORDER_STATS response for 3 polls; stopping recorder poll (last stats at ${this.lastFileRecorderStatsTime ? new Date(this.lastFileRecorderStatsTime).toISOString() : 'never'})`);
            }
          });
        }

        // this.lastHeartbeatTime = Date.now()
        // this.heartbeatCheck = setTimeout(()=> {
        //   this.responding = false;
        //   this.respondingStateSubject.next(this.responding);
        //   this.updateDeviceStateColor();
        // }, 1500)
    })

    this.updateTimeIntervalSubscription = this.updateTimeInterval.subscribe(()=>{
      var tempDate = new Date(this.deviceTime)
      tempDate.setMilliseconds(tempDate.getMilliseconds() + 250)
      this.deviceTime = tempDate
    })

    this.connectionStatsIntervalSubscription = this.connectionStatsInterval.subscribe(()=> {
      this.rx_s = this.connectionStats.received_bytes - this.lastRxCount
      this.lastRxCount = this.connectionStats.received_bytes

      this.tx_s = this.tx_bytes - this.lastTxCount
      this.lastTxCount = this.tx_bytes
    })
  }

  // Shared BLE-write mutex. Web Bluetooth serializes GATT ops per connection and
  // throws "GATT operation already in progress" if two are outstanding, so anything
  // that writes to this device (NUS chunks here, mcumgr OTA writes over on
  // McumgrCap) must acquire this before its BleClient.write* call and clear it in
  // both the resolve and reject paths.
  writePromise: Promise<void>|null = null

  async acquireBleWriteSlot(): Promise<void> {
    // Simple spin-wait — the mutex holders are short-lived BLE writes (<50ms),
    // and clearPacketBuffer runs on a 2ms tick so busy-waiting at the same
    // cadence keeps things simple without pulling in an async lock lib.
    while(this.writePromise != null) {
      try { await this.writePromise; } catch { /* prior write's error is not ours */ }
    }
  }

  // --- OTA keepalive / charger check (see the note above the PhysensDevice class) ---
  // Last time an OTA-keepalive battery poll was written/acked, for the "seconds since last
  // keepalive" diagnostic logged on link-loss (physens-ble-device.model.ts DISCONNECTED case).
  lastOtaKeepaliveSentAt: number | null = null;
  lastOtaKeepaliveAckAt: number | null = null;
  private otaKeepaliveInFlight = false;

  // Deliberately NOT routed through sendPacket()/packetBuffer/clearPacketBuffer — that pipeline
  // is gated off entirely while updateInProgress (see clearPacketBuffer) specifically so NUS
  // traffic can't interleave with mcumgr's own chunk writes on the same GATT connection. This
  // re-opens a single, narrowly-scoped exception: at most one GET_BATCHRGSTATE per tick, written
  // directly through the same writePromise mutex mcumgr uses, so it still can never race a
  // chunk write/ack — if the slot is busy this tick is skipped outright, never queued. The
  // response is published on battery_state_subject exactly like getBatChargeState(), which is
  // what lets DatasnapDeviceService abort the OTA when the device is put on the charger.
  private async sendOtaBatteryPoll(): Promise<void> {
    const label = this.deviceName ?? this.deviceId;
    if(this.removed) return;
    if(this.expectingReboot) {
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive skipped (expectingReboot) for ${label}`);
      return;
    }
    if(this.deviceState !== Device_State.READY_FOR_PACKETS) {
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive skipped (not READY_FOR_PACKETS) for ${label}`);
      return;
    }
    if(this.otaKeepaliveInFlight) {
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive skipped (previous battery poll still pending) for ${label}`);
      return;
    }
    if(this.writePromise != null) {
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive skipped (write slot busy — chunk write/ack in flight) for ${label}`);
      return;
    }

    const pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.GET_BATCHRGSTATE;
    pkt.calcBytes();

    this.otaKeepaliveInFlight = true;

    const resultPromise = new Promise<ResponsePacket>((resolve, reject) => {
      this.packetResponseCallbacks[pkt.mPacketNumber!] = [resolve, reject];
      setTimeout(() => {
        if (this.packetResponseCallbacks[pkt.mPacketNumber!]) {
          delete this.packetResponseCallbacks[pkt.mPacketNumber!];
          reject(new Error('OTA keepalive timeout'));
        }
      }, 3000);
    });
    resultPromise.catch(() => undefined); // timeouts are swallowed silently (see below)

    try {
      this.writePromise = this.txBytes(new DataView(pkt.mBytes!.buffer));
      this.lastOtaKeepaliveSentAt = Date.now();
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive sent battery poll (#${pkt.mPacketNumber}) for ${label} at ${new Date(this.lastOtaKeepaliveSentAt).toISOString()}`);
      await this.writePromise;
      this.writePromise = null;
    } catch (e) {
      this.writePromise = null;
      if (OTA_DIAG) console.log(`[FWUpdate][DIAG] OTA keepalive write failed for ${label}: ${e}`);
    }

    resultPromise.then((resp) => {
      this.lastOtaKeepaliveAckAt = Date.now();
      if(resp.batteryState == null) return;
      this.publishBatteryState(resp.batteryState);
    }).catch(() => {
      // Swallowed by design — a missed ack during OTA isn't actionable here, the next
      // 1s tick just tries again.
    }).finally(() => {
      this.otaKeepaliveInFlight = false;
    });
  }

  private publishBatteryState(state: BLE_CHARGE_ICON) {
    // Logged on change only, so field logs show exactly what the device reported when it
    // was placed on / taken off the charger (bug 20264).
    if(state !== this.battery_state) {
      console.log(`[Battery] ${this.deviceName ?? this.deviceId}: ${bleChargeIconToString(this.battery_state)} -> ${bleChargeIconToString(state)} (raw=${state}, updateInProgress=${this.updateInProgress})`);
    }
    this.battery_state = state;
    this.battery_state_string = bleChargeIconToString(state);
    this.battery_state_subject.next(state);
  }

  // One-shot charger check for the OTA steps that run while the 1s poll above is paused by
  // expectingReboot: the reset, and the post-reboot verify/confirm. Waits for the write slot
  // instead of skipping, and publishes the reply on battery_state_subject so
  // DatasnapDeviceService aborts the update exactly as it does for the periodic poll.
  // Resolves null if the device isn't ready or doesn't answer within timeoutMs.
  async readChargeStateForOta(timeoutMs = 3000): Promise<BLE_CHARGE_ICON | null> {
    if(this.removed || this.deviceState !== Device_State.READY_FOR_PACKETS) return null;
    await this.acquireBleWriteSlot();
    if(this.removed) return null;

    const pkt = new CommandPacket();
    pkt.mPacketNumber = this.packet_number++;
    pkt.pktSubType = PACKET_SUB_TYPE.GET_BATCHRGSTATE;
    pkt.calcBytes();
    const resultPromise = new Promise<ResponsePacket>((resolve, reject) => {
      this.packetResponseCallbacks[pkt.mPacketNumber!] = [resolve, reject];
      setTimeout(() => {
        if (this.packetResponseCallbacks[pkt.mPacketNumber!]) {
          delete this.packetResponseCallbacks[pkt.mPacketNumber!];
          reject(new Error('OTA charge state read timeout'));
        }
      }, timeoutMs);
    });
    resultPromise.catch(() => undefined);

    try {
      this.writePromise = this.txBytes(new DataView(pkt.mBytes!.buffer));
      await this.writePromise;
    } catch (e) {
      delete this.packetResponseCallbacks[pkt.mPacketNumber!];
      console.warn(`[FWUpdate] charge state read write failed for ${this.deviceName ?? this.deviceId}: ${e}`);
      return null;
    } finally {
      this.writePromise = null;
    }

    const resp = await resultPromise.catch(() => null);
    if(resp?.batteryState == null) {
      console.warn(`[FWUpdate] charge state read got no answer from ${this.deviceName ?? this.deviceId}`);
      return null;
    }
    console.log(`[FWUpdate] charge state before OTA step: ${bleChargeIconToString(resp.batteryState)}`);
    this.publishBatteryState(resp.batteryState);
    return resp.batteryState;
  }

  // Waits for the device to come back to READY_FOR_PACKETS after a mid-transfer GATT
  // drop, instead of letting the caller burn through its retry budget while the auto-
  // reconnect backoff (physens-ble-device.model.ts) is still working in the background.
  // Resolves true once ready again, false if timeoutMs elapses first.
  protected waitForReconnect(timeoutMs = 90000): Promise<boolean> {
    if(this.deviceState === Device_State.READY_FOR_PACKETS) return Promise.resolve(true);
    return new Promise((resolve) => {
      const sub = this.deviceStateSubject.subscribe((state) => {
        if(state === Device_State.READY_FOR_PACKETS) {
          clearTimeout(timer);
          sub.unsubscribe();
          resolve(true);
        }
      });
      const timer = setTimeout(() => {
        sub.unsubscribe();
        resolve(false);
      }, timeoutMs);
    });
  }

  // Drains the outgoing byte queue and rejects any packets awaiting a response.
  // Called on GATT disconnect so stale queued commands/awaits from before the
  // drop don't leak into a fresh post-reconnect session.
  protected clearPendingWrites(reason: string) {
    this.packetBuffer = [];
    this.sendData = new Uint8Array();
    for(const key of Object.keys(this.packetResponseCallbacks)) {
      const [, reject] = this.packetResponseCallbacks[Number(key)];
      reject(new Error(reason));
      delete this.packetResponseCallbacks[Number(key)];
    }
  }

  // Called right as an OTA upload starts (updateInProgress flips true). Any
  // housekeeping packet queued (or already awaiting a response) from the instant
  // before that — e.g. a keepalive-loop tick that fired just before the flag
  // flipped — would otherwise sit in clearPacketBuffer()'s updateInProgress gate
  // until its own 30s sendPacket() timeout fires, surfacing as a spurious
  // "X failed: Response timeout..." warning with no bearing on the actual upload.
  // Cancelling it immediately with the recognized OTA_CANCEL_REASON lets
  // sendPacketWithoutAwaiting's catch (and the few external .catch sites on
  // getSystemStatus/setLEDColor) swallow it silently instead.
  cancelPendingHousekeeping() {
    if(this.pendingDateCheckTimer !== null) {
      clearTimeout(this.pendingDateCheckTimer);
      this.pendingDateCheckTimer = null;
    }
    this.clearPendingWrites(PhysensDevice.OTA_CANCEL_REASON);
  }
  private packetBuffer = Array<Packet>()
  private sendData = new Uint8Array();
  private packetResponseCallbacks: {[pktNum: number]: [(value: ResponsePacket) => void, (reason?: any) => void]} = {}

  // Web Bluetooth exposes no way to query the negotiated ATT MTU, so mtuLength above is
  // a best-effort guess. If writes keep failing we shrink it defensively instead of
  // assuming the guess is safe for every connection/chipset.
  private consecutiveWriteFailures = 0;
  private readonly minMtuLength = 20;

  private sendPacket(pkt: Packet|CommandPacket, responseTimeout=30000): PacketResponsePromise {
    this.packetHistory.push([CommunicationType.TX_PACKET, pkt])
    this.packetBuffer.push(pkt)
    const packetResponsePromise: PacketResponsePromise = new Promise((resolve, reject) => {
      const startTime = performance.now();
      this.packetResponseCallbacks[pkt.mPacketNumber!] = [(respPkt: ResponsePacket) => {
        resolve([respPkt, pkt, {
          elapsedTime: performance.now() - startTime
        }]);
      }, reject];
      setTimeout(() => {
        if (this.packetResponseCallbacks[pkt.mPacketNumber!]) {
          reject(new Error('Response timeout for packet sub type ' + pkt.pktSubType));
          delete this.packetResponseCallbacks[pkt.mPacketNumber!];
        }
      }, responseTimeout);
    });
    // Prevent unhandled-rejection noise if the caller never attaches its own
    // .catch (several fire-and-forget sends don't) — this doesn't swallow the
    // rejection for callers that do handle it, since each .then/.catch on a
    // promise fires independently.
    packetResponsePromise.catch(() => undefined);
    // this.clearPacketBuffer()
    return packetResponsePromise;
  }

  private sendPackets(pkts: Packet[]) {
    for(let pkt of pkts) {
      this.packetHistory.push([CommunicationType.TX_PACKET, pkt])
      this.packetBuffer.push(pkt)
    }
    // this.clearPacketBuffer()
  }

  txBytes(bytes: DataView): Promise<void> {
    return Promise.resolve();
  }

  async canTxBytes(): Promise<boolean> {
    return true;
  }

  private checkPacketBuffer = () => {
    while(this.packetBuffer.length > 0) {
      //append to the send
      let pkt = this.packetBuffer.shift()
      let temp = new Uint8Array(this.sendData.length + pkt!.mBytes!.length)
      temp.set(this.sendData)
      temp.set(pkt!.mBytes!, this.sendData.length)

      this.sendData = temp
      
    }
  }

  private async clearPacketBuffer() {
    // A removed instance (e.g. torn down by the charger abort) must never drain its queue,
    // even if a later currentlyUpdating=false clears updateInProgress on it.
    if(this.removed) return;
    if(this.writePromise != null) return;
    // if(!(await this.canTxBytes())) return;
    if(this.deviceState != Device_State.READY_FOR_PACKETS) return;
    // Web Bluetooth GATT allows only one write op in flight per connection. During
    // an OTA the mcumgr chunk-upload writes hit the same GATT server as our NUS
    // txBytes() writes here — firing both concurrently throws
    // "NetworkError: GATT operation already in progress" and desyncs the upload.
    // Keepalive/battery/file-recorder polling is already gated on updateInProgress
    // upstream; gate the buffered-write drain the same way so nothing sneaks past.
    if(this.updateInProgress) return;

    if(this.sendData.byteLength > 0) {
      let toSend = this.sendData.slice(0, this.mtuLength)
      this.sendData = this.sendData.slice(this.mtuLength)
      var dv = new DataView(toSend.buffer)
      
      if(this.logTxBytes) {
        let temp = new Uint8Array(this.txBytesLog.length + dv.byteLength)
        temp.set(this.txBytesLog)
        temp.set(new Uint8Array(dv.buffer), this.txBytesLog.length)
        this.txBytesLog = temp
        // console.log("TX BYTES LOG: ", Array.from(this.txBytesLog).map(b => b.toString(16).padStart(2, '0')).join(' '))

      }
      this.writePromise = this.txBytes(dv);

      this.writePromise.then(()=>{
        this.writePromise = null
        this.consecutiveWriteFailures = 0
        // this.clearPacketBuffer(newLeftover)
      })

      this.writePromise.catch((e)=>{
        console.error(`Error sending packet (chunk size ${toSend.length}): `, e)
        this.writePromise = null

        // Don't silently lose these bytes: sendData is a single framed byte stream, so
        // dropping a chunk here desyncs everything sent after it. Put it back at the
        // front of the queue so the next interval tick retries it.
        const requeued = new Uint8Array(toSend.length + this.sendData.length)
        requeued.set(toSend)
        requeued.set(this.sendData, toSend.length)
        this.sendData = requeued

        // Web Bluetooth has no MTU introspection API (see mtuLength comment above), so if
        // writes keep failing, shrink the chunk size defensively rather than assuming the
        // hardcoded guess is safe for this connection.
        this.consecutiveWriteFailures++
        if (this.consecutiveWriteFailures >= 3 && this.mtuLength > this.minMtuLength) {
          this.mtuLength = Math.max(this.minMtuLength, Math.floor(this.mtuLength / 2))
          console.warn(`Repeated packet send failures; reducing chunk size to ${this.mtuLength} bytes`)
          this.consecutiveWriteFailures = 0
        }
      })
      this.tx_bytes += dv.byteLength
      return
    }
  }

  private byteBuffer: Uint8Array = new Uint8Array()

  public rxBytes(data: DataView) {

    this.connectionStats.received_bytes += data.buffer.byteLength
    

    const source1 = new Uint8Array(this.byteBuffer)
    const source2 = new Uint8Array(data.buffer)
    const destination = new Uint8Array(source1.length + source2.length)

    destination.set(source1)
    destination.set(source2, source1.length)

    this.byteBuffer = destination

    this.parseBytes()
  }

  private packetParsed(pkt: Packet|ResponsePacket|OutputsetPacket|AppResponsePacket|AppStatusPacket) {
    if(pkt.mPacketType != PACKET_TYPE.OUTPUT_SET) {
      this.packetHistory.push([CommunicationType.RX_PACKET, pkt])
    }
    this.packet_subject.next(pkt)
  }

  private parseBytes() {
    while(true) {
      var startIndex = this.byteBuffer.findIndex((value, index, obj)=>value==0xCC)

      if(startIndex < 0) {
        // let discarded_bytes = this.byteBuffer.slice(0)
        // this.byteBuffer = new Uint8Array()
        // this.packetHistory.push([CommunicationType.DISCARD_BYTES, discarded_bytes])

        return;
      }

      this.connectionStats.discarded_bytes += startIndex

      if(startIndex > 0) {
        let discardedBytes = this.byteBuffer.slice(0, startIndex)

        this.packetHistory.push([CommunicationType.DISCARD_BYTES, discardedBytes])
      }
      

      this.byteBuffer = this.byteBuffer.slice(startIndex)

      if(this.byteBuffer.length > 8) {
        var payloadLen = this.byteBuffer[1]

        if(this.byteBuffer.length >= (payloadLen*4 + 8)) {

          var pkt = Packet.fromBytes(this.byteBuffer.slice(0, payloadLen*4 + 8))

          if(pkt.validCRC) {
            this.byteBuffer = this.byteBuffer.slice(payloadLen*4 + 8)
            switch(pkt.mPacketType) {
              case PACKET_TYPE.RESPONSE: {
                var [success, resp_pkt] = ResponsePacket.fromPacket(pkt, this.publishers)
                if(success) {
                  this.packetParsed(resp_pkt)

                  if(resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_DEVINFO) {
                    this.device_info = {
                      deviceSN: resp_pkt.deviceSN!,
                      projectID: resp_pkt.projectID!,
                      deviceType: resp_pkt.deviceType!,
                      roleID: resp_pkt.roleID!,
                      appSpecInfo: resp_pkt.appSpecInfo!,
                      fwReleaseType: resp_pkt.fwReleaseType!,
                      fwMajorVersion: resp_pkt.fwMajorVersion!,
                      fwMinorVersion: resp_pkt.fwMinorVersion!,
                      fwBugVersion: resp_pkt.fwBugVersion!,
                      hwProductID: resp_pkt.hwProductID!,
                      hwSubcomponentID: resp_pkt.hwSubcomponentID!,
                      hwMajorVersion: resp_pkt.hwMajorVersion!,
                      hwMinorVersion: resp_pkt.hwMinorVersion!,
                      dfuType: resp_pkt.dfuType!,
                      dfuMajorVersion: resp_pkt.dfuMajorVersion!,
                      dfuMinorVersion: resp_pkt.dfuMinorVersion!,
                      dfuBugVersion: resp_pkt.dfuBugVersion!,
                      bootType: resp_pkt.bootType!,
                      bootMajorVersion: resp_pkt.bootMajorVersion!,
                      bootMinorVersion: resp_pkt.bootMinorVersion!,
                      bootBugVersion: resp_pkt.bootBugVersion!,
                      compileTime: resp_pkt.compileTime!,
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_TIME) {
                    this.deviceTime.setHours(resp_pkt.hour!)
                    this.deviceTime.setMinutes(resp_pkt.minute!)
                    this.deviceTime.setSeconds(resp_pkt.second!)
                    this.deviceTime.setMilliseconds(resp_pkt.fracSeconds!*1000/255)

                    const now = new Date();
                    const deviceTime = this.deviceTime;
                    const diffMs = Math.abs(now.getTime() - deviceTime.getTime());
                    // console.log(`Device Time: ${deviceTime.toISOString()}, Host Time: ${now.toISOString()}, Difference: ${diffMs} ms`);
                    this.deviceTimeError = diffMs;

                    //This is the packet for the heartbeat. Denote response now
                    // if(this.heartbeatCheck) {
                    //   clearTimeout(this.heartbeatCheck)
                    // }
                    // let oldresponding = this.responding
                    // this.responding = (Date.now() - this.lastHeartbeatTime) < 500
                    // if(oldresponding != this.responding) {
                    //   this.respondingStateSubject.next(this.responding)
                    // }
                    
                    this.updateDeviceStateColor()
                    
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_DATE) {
                    this.deviceTime.setFullYear(2000+resp_pkt.year!)
                    this.deviceTime.setMonth(resp_pkt.month!-1)
                    this.deviceTime.setDate(resp_pkt.day!)
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PUBLISHERS) {
                    this.publishers = resp_pkt.publishers
                    this.getPublisherNames()
                    this.getOutputs()
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PUBLISHER_NAME) {
                    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == resp_pkt.pubId})
                    if(pub != null) {
                      pub.name = resp_pkt.pubName!
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUTS) {
                    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == resp_pkt.pubId})
                    if(pub != null) {
                      pub.outputs = resp_pkt.outputList
                      this.getOutputNames(pub.pubId)
                      this.getOutputUnits(pub.pubId)
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUT_NAME) {
                    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == resp_pkt.pubId})
                    if(pub != null) {
                      let output = pub.outputs.find((value, index, obj)=> {return value.index == resp_pkt.outputIdx})
                      if(output != null) {
                        output.name = resp_pkt.outputName!
                      }
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_OUTPUT_UNITS) {
                    let pub = this.publishers.find((value, index, obj)=> {return value.pubId == resp_pkt.pubId})
                    if(pub != null) {
                      let output = pub.outputs.find((value, index, obj)=> {return value.index == resp_pkt.outputIdx})
                      if(output != null) {
                        output.units = resp_pkt.outputUnits!
                      }
                    }
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PARAMETER_SETS) {
                    this.parameterSets =  resp_pkt.parameterSets!
                    this.getParametersForSets()
                    this.getInfoForParameterSets()


                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PARAMETERS) {
                    // //start storing the list here
                    // console.log("got params")
                    let setIndex = this.parameterSets.findIndex((paramSet)=>paramSet.setTypeId==((resp_pkt.paramSetUUID!&0xFFF0)>>4))
                    if(setIndex >-1) {
                      this.parameterSets[setIndex].parameters = resp_pkt.parameters
                      this.getParameterInfoForSet(this.parameterSets[setIndex])
                    } else {
                      console.log("couldn't find parameter set this belongs to")
                    }

                    // this.parameters = resp_pkt.parameters
                    // this.getParametersInfo()
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.GET_PARAMETER_INFO) {
                    let setTypeId = ((resp_pkt.paramUUID!&0xFFF00000)>>20)&0x0FFF
                    let paramUUID = (resp_pkt.paramUUID!&0x0000FFFF)

                    let param = this.parameterSets.find((paramSet)=>paramSet.setTypeId==setTypeId)?.parameters.find((param)=>param.UUID == paramUUID)

                    if(param){
                      param.name = resp_pkt.paramName!
                      param.numberBytes = resp_pkt.paramNumBytes!
                      param.numberElements = resp_pkt.paramNumElements!
                      param.paramType = resp_pkt.paramType!
                    }
                    
                  } else if (resp_pkt.mPktSubType == PACKET_SUB_TYPE.ENABLE_FILERECORDER) {
                    if(this.enableFileRecorderHandles != null) {
                      let [resolve, reject] = this.enableFileRecorderHandles
                      this.enableFileRecorderHandles = undefined
                      if(resp_pkt.result == RESPONSE_RESULT.SUCCESS) {
                        resolve(null)
                      } else {
                        reject(null)
                      }
                    }
                    
                  }

                  
                }
                const responseCallback = this.packetResponseCallbacks[resp_pkt.mPacketNumber!];
                if(responseCallback) {
                  responseCallback[0](resp_pkt);
                  delete this.packetResponseCallbacks[resp_pkt.mPacketNumber!];
                }
                break;
              }
              case PACKET_TYPE.OUTPUT_SET: {
                var [success, oset_pkt] = OutputsetPacket.fromPacket(pkt, this.publishers)
                if(success) {
                  this.packetParsed(oset_pkt)
                }
                break;
              }
              case PACKET_TYPE.APP_RESPONSE: {
                var [success, appresp_pkt] = AppResponsePacket.fromPacket(pkt)
                if(success) {
                  this.packetParsed(appresp_pkt)
                  const responseCallback = this.packetResponseCallbacks[appresp_pkt.mPacketNumber!];
                  if(responseCallback) {
                    responseCallback[0](appresp_pkt as ResponsePacket);
                    delete this.packetResponseCallbacks[appresp_pkt.mPacketNumber!];
                  }
                }
                break;
              }
              case PACKET_TYPE.APP_STATUS: {
                var [success, appstat_pkt] = AppStatusPacket.fromPacket(pkt)
                if(success) {
                  this.packetParsed(appstat_pkt)
                }
                break;
              }
              default:
                this.packetParsed(pkt)
                break;
            }


          } else {
            this.connectionStats.discarded_bytes += 1
            this.connectionStats.invalid_CRC_packets += 1

            let discardedBytes = this.byteBuffer.slice(0, 1)

            this.packetHistory.push([CommunicationType.DISCARD_BYTES, discardedBytes])

            this.byteBuffer = this.byteBuffer.slice(1)
          }

        } else {
          break;
        }
      } else {
        break;
      }


    }
    
  }

  
}

export class ConnectionStatistics {
  discarded_bytes = 0
  received_bytes = 0
  invalid_CRC_packets = 0
  missed_packets = 0
  valid_packets = 0
}

export enum Device_State {
  DISCONNECTED = "Disconnected",
  CONNECTED = "Connected",
  NOTIFYING = "Notifying",
  READY_FOR_PACKETS = "Ready for Packets"
}

export enum PORT_UUID {
  SELF = 0x0000,
  ALL = 0x0001,

  UART0 = 0x1000,
  UART1 = 0x1001,
  UART2 = 0x1002,
  UART3 = 0x1003,

  USB0 = 0x1010,
  USB1 = 0x1011,
  USB2 = 0x1012,
  USB3 = 0x1013,

  BLE_UART0 = 0x1020,
  BLE_UART1 = 0x1021,
  BLE_UART2 = 0x1022,
  BLE_UART3 = 0x1023,

  FILE0 = 0x1030,
  FILE1 = 0x1031,
  FILE2 = 0x1032,
  FILE3 = 0x1033,

  ETH0 = 0x1040,
  
  NA = 0xFFFF,
}